/**
 * Gift with purchase (GWP) auto-add.
 *
 * When a trigger product (e.g. The FFFull Collection) is in the cart, the configured
 * gift variants are added automatically, tagged with the hidden line property `_gwp`.
 * When the trigger product leaves the cart, those tagged gift lines are removed.
 *
 * - Pricing is NOT handled here: the automatic "Buy X get Y" discount makes the gifts free.
 * - Each gift is added once per cart (tracked with a hidden cart attribute), so if a customer
 *   deliberately removes one gift it is not forced back in. If all gift lines are gone while the
 *   trigger is still in the cart, the state is treated as stale and the gifts are re-added.
 * - Only lines this script added (`_gwp` property) are ever removed. Items a customer added
 *   themselves are never touched.
 *
 * Config is rendered by snippets/gift-with-purchase.liquid into #gwp-config.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const SOURCE = 'gift-with-purchase';

function readConfig() {
  const el = document.getElementById('gwp-config');
  if (!el) return null;
  try {
    const cfg = JSON.parse(el.textContent || '{}');
    if (!cfg.enabled || !Array.isArray(cfg.rules) || cfg.rules.length === 0) return null;
    return cfg;
  } catch (e) {
    console.warn('[GWP] invalid config', e);
    return null;
  }
}

const config = readConfig();

let running = false;
let rerun = false;

async function getCart() {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function post(path, body) {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.description || json.message || `${path} ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return json;
}

/** Re-render the cart drawer / cart page and the cart bubble after our changes. */
async function refreshCartUI() {
  const sections = cartSectionIds();
  let sectionHtml = {};
  if (sections.length) {
    try {
      // cart/update.js with an empty update is a cheap way to get fresh section HTML.
      const res = await post('cart/update.js', { updates: {}, sections: sections.join(','), sections_url: window.location.pathname });
      sectionHtml = res.sections || {};
    } catch (e) {
      /* fall through: components will re-render themselves without sections */
    }
  }
  const cart = await getCart();
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE, {
      source: SOURCE,
      itemCount: cart.item_count,
      sections: sectionHtml,
    })
  );
}

function isGiftLine(item, rule) {
  return item.properties && item.properties._gwp === rule.id;
}

async function applyRule(rule, cart) {
  // Hidden cart attribute listing the gift variant IDs we've already added to this cart,
  // e.g. "43654335856880,46694599917808". Used so a gift the customer deletes isn't forced back in.
  const attrKey = `_gwp_${rule.id}`;
  const triggerIds = new Set(rule.triggerVariantIds.map(Number));
  const giftIds = rule.giftVariantIds.map(Number);
  const hasTrigger = cart.items.some(
    (item) => triggerIds.has(item.variant_id) && !(item.properties && item.properties._gwp)
  );
  const giftLines = cart.items.filter((item) => isGiftLine(item, rule));
  const rawGiven = (cart.attributes && cart.attributes[attrKey]) || '';
  let given = new Set(
    String(rawGiven)
      .split(',')
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v > 0)
  );
  let changed = false;

  if (hasTrigger) {
    // If none of our gift lines are in the cart, any recorded state is stale (cart cleared,
    // checkout edited, etc.) - start fresh so the gifts are added again.
    if (giftLines.length === 0) given = new Set();
    const present = new Set(giftLines.map((i) => i.variant_id));
    const toAdd = giftIds.filter((id) => !present.has(id) && !given.has(id));
    // Add one at a time so one out-of-stock gift doesn't block the others.
    for (const id of toAdd) {
      try {
        await post('cart/add.js', { items: [{ id, quantity: 1, properties: { _gwp: rule.id } }] });
        changed = true;
      } catch (e) {
        console.warn(`[GWP] could not add gift ${id}:`, e.message);
      }
      given.add(id); // record even on failure so we don't retry on every cart change
    }
    present.forEach((id) => given.add(id));
    const next = Array.from(given).join(',');
    if (next !== rawGiven) await post('cart/update.js', { attributes: { [attrKey]: next } });
  } else if (giftLines.length || rawGiven) {
    if (giftLines.length) {
      const updates = {};
      giftLines.forEach((line) => (updates[line.key] = 0));
      await post('cart/update.js', { updates });
      changed = true;
    }
    await post('cart/update.js', { attributes: { [attrKey]: '' } });
  }
  return changed;
}

async function sync() {
  if (!config) return;
  if (running) {
    rerun = true;
    return;
  }
  running = true;
  try {
    let changed = false;
    const cart = await getCart();
    for (const rule of config.rules) {
      // Re-read the cart between rules only if something changed.
      const current = changed ? await getCart() : cart;
      changed = (await applyRule(rule, current)) || changed;
    }
    if (changed) await refreshCartUI();
  } catch (e) {
    console.warn('[GWP] sync failed', e);
  } finally {
    running = false;
    if (rerun) {
      rerun = false;
      sync();
    }
  }
}

if (config) {
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.source === SOURCE || detail?.sourceId === SOURCE) return;
    if (detail?.data?.didError) return;
    sync();
  });
  // Also reconcile on page load (e.g. cart changed in another tab or via a cart link).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync());
  } else {
    sync();
  }
}
